import { Component, Inject, LOCALE_ID, ViewChild, OnInit, OnDestroy, AfterContentInit, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { Store } from '@ngrx/store';
import { State, SortDescriptor } from '@progress/kendo-data-query';
import { Observable, Subscription } from 'rxjs';
import { ReportService } from '../shared/services/report.service';
import { UserService } from 'src/app/admin/user/shared/services/user.service';
import { ReportCommonService } from '../shared/services/report-common.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FiltersComponent } from '../filters/filters.component';
import { WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { SortInfo } from '../../shared/models/queryparams/sortinfo.model';
import { SystemConfigValue } from '../../admin/system-configuration/shared/models/system-config-value';
import { TranslateService } from '@ngx-translate/core';
import { ReportDataViewRequest } from '../shared/models/data-view.model';
import { LocationFilterOption } from '../shared/models/filter-options-model';

@Component({
  selector: 'app-lobby-data-view',
  templateUrl: './lobby-data-view.component.html',
  styleUrls: ['./lobby-data-view.component.scss']
})

export class LobbyDataViewComponent implements OnInit, OnDestroy, AfterViewInit {

  public dataViews: any[] = [];
  public selectedLocations: LocationFilterOption[] =[];
  public defaultSort: SortDescriptor[] = [{ field: '', dir: 'asc' }];
  public sort: SortDescriptor[] = [{ field: '', dir: 'asc' }];
  public dataViewForm!: FormGroup;
  public pageInitialized = false;
  private windowRef?: WindowRef;
  public LastRefreshed: string = '';
  public eagerLoad = true;
  public isLoading = false;
  public loadingTimer: Subscription = new Subscription();
  public currentLoadingPromptState: number = 0;
  interval: any;
  public loadingPrompt: string = '';
  public initialReportExecutionComplete: boolean = false;
  public loadingDataText: string = '';
  public gridData: GridDataResult = {data: [], total: 0};
  public pageSize: number = 20;
  public skip: number = 0;
  public columns: any[] = [];
  public locationDropdownSettings = {
    singleSelection: false,
    idField: 'locationId',
    textField: 'locationName',
    itemsShowLimit: 3,
    noDataAvailablePlaceholderText: this.translateService.instant('_Dashboard_No_Data_Available_Message_Text_'),
    unSelectAllText: this.translateService.instant('_General_Unselect_All_Ts_Text_'),
    selectAllText: this.translateService.instant('_General_Select_All_Text_'),
  };

  @ViewChild('grid') grid?: GridComponent;

  constructor(public reportService: ReportService, 
    public fb: FormBuilder, 
    private userService: UserService, 
    private route: ActivatedRoute,
    private router: Router, 
    public commonData: ReportCommonService, 
    private windowService: WindowService, 
    @Inject(LOCALE_ID) public locale: string,
    private store: Store<State>,
    private translateService: TranslateService) { }

  ngOnInit() {
    this.translateService.get("_Report_Loading_Data_Text_").subscribe((res) => {
      this.loadingDataText = res;
    });
    this.initializeForm();
    this.pageInitialized = true;
    this.initialReportExecutionComplete = false;
    this.getDataViewFilters();
  }
  public resetGridOptions() {  
    this.skip = 0;
  }

  ngOnDestroy() {
    if (this.windowRef != null) {
      this.windowRef.close();
    }
    if (this.commonData.applyEventSubscribed == true) {
      this.commonData.applyEventSubscribed = false;
    }
  }

  ngAfterViewInit() {
    this.commonData.applyEventSubscribed = false;
    window.scrollTo(0, 0);
  }

  public fromDateChange() {
    this.commonData.reportStartDate = this.dataViewForm.get("from")?.value;
    if (this.commonData.reportStartDate > this.commonData.reportEndDate) {
      this.commonData.reportStartDate = this.commonData.reportEndDate;
      this.dataViewForm.get("from")?.setValue(this.commonData.reportStartDate);
    }
  }

  public toDateChange() {
    this.commonData.reportEndDate = this.dataViewForm.get("to")?.value;
    if (this.commonData.reportEndDate < this.commonData.reportStartDate) {
      this.commonData.reportEndDate = this.commonData.reportStartDate;
      this.dataViewForm.get("to")?.setValue(this.commonData.reportEndDate);
    }
  }

  private initializeForm(): void {
    this.dataViewForm = this.fb.group({
      dataView: [null],
      from: [null, Validators.required],
      to: [null, Validators.required],
      locations: [[]]
    });

    // Set default dates after form is created
    const startDate = new Date(new Date().setHours(0, 0, 0, 0)); //Current Date
    const endDate = new Date(new Date().setHours(0, 0, 0, 0));

    this.commonData.reportStartDate = startDate;
    this.commonData.reportEndDate = endDate;

    this.dataViewForm.patchValue({
      from: startDate,
      to: endDate
    });
  }

  public runReport() {
    this.isLoading = true;
    this.currentLoadingPromptState = 0;
    this.loadingPrompt = this.loadingDataText;
    this.interval = setInterval(() => {
      this.nextLoadingPrompt();
      if (this.currentLoadingPromptState > 10) {
        this.currentLoadingPromptState = 0;
        this.loadingPrompt = this.loadingDataText;
      } else {
        this.currentLoadingPromptState++;
      }
    }, 1000);

    const dataViewId = this.dataViewForm.get('dataView')?.value;
    const selectedLocationIds = this.dataViewForm.get('locations')?.value || [];
    const fromDate = this.dataViewForm.get('from')?.value;
    const toDate = this.dataViewForm.get('to')?.value;
    var dataViewRequest = new ReportDataViewRequest(dataViewId, selectedLocationIds, fromDate,
      toDate, { Page: (this.skip / this.pageSize) + 1, PageSize: this.pageSize }, this.getSortInfo());

    this.reportService.getDataViewGrid(dataViewRequest).subscribe((response) => {
      clearInterval(this.interval);
      this.columns = [];
      if (response?.data?.length > 0) {
        const firstRow = response.data[0];

        this.columns = Object.keys(firstRow).map((key, index) => ({
          field: key,
          sortable: !(key == "first Name" || key == "last Name"),
          title: this.toTitleCase(key),
          width: Math.max(62, 27 + (key.length * 7.4)),
          sortIndex: index + 1
        }));
      }
      else if (response?.columns?.length > 0) {
        this.columns = response.columns.map((column: any) => ({
          field: column.name,
          sortable: !(column.name == "First Name" || column.name == "Last Name"),
          title: column.name,
          width: Math.max(62, 27 + (column.name.length * 7.4)),
          sortIndex: column.sortIndex
        }));
      }
      this.gridData = { data: response.data, total: response.totalRowCount };
      this.grid?.autoFitColumns();
      this.isLoading = false;
    }, error => {
      clearInterval(this.interval);
      console.error('Running report failed.');
      console.error(error);
      this.isLoading = false;
    });
  }

  public exportToCsv(): any {
    const dataViewId = this.dataViewForm.get('dataView')?.value;
    const fromDate = this.dataViewForm.get('from')?.value;
    const toDate = this.dataViewForm.get('to')?.value;
    const selectedLocations = this.dataViewForm.get('locations')?.value || [];
    var dataViewExportRequest = new ReportDataViewRequest(
      dataViewId,
      selectedLocations,
      fromDate,
      toDate,
      { Page: (this.skip / this.pageSize) + 1, PageSize: this.pageSize },
      this.getSortInfo(),
      this.isAllLocationsSelected()
    );

    this.reportService.getDataViewGridExport(dataViewExportRequest).subscribe((response) => {
      const contentDisposition = response.headers.get('Content-Disposition')
        || response.headers.get('content-disposition');

      let filename = ''; // default fallback

      if (contentDisposition) {
        // Example header: attachment; filename="My Report - 2025-11-30.csv"
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      let blob = new Blob([response.body], { type: 'application/csv' });
      var downloadURL = window.URL.createObjectURL(blob);
      var link = document.createElement('a');
      //var filename = "LobbyDataView.csv";
      link.href = downloadURL;
      link.download = filename;
      link.click();
    }, error => {
      console.error('Running report failed.');
      console.error(error);
    });
  }

  public pageChange({ skip, take }: PageChangeEvent): void {
    this.skip = skip;
    this.pageSize = take;
    this.runReport();
  }

  public getDataViewFilters() {
    this.reportService.getDataViewFilter().subscribe((filter) => {
      this.dataViews = filter.dataViews;
      this.commonData.locations = filter.locations;
      if (this.dataViews.length > 0) {
        this.dataViewForm.get("dataView")?.setValue(this.dataViews[0].id);
        this.dataViewForm.get("locations")?.setValue(filter.locations);
        this.runReport();
      }
    })
  }
  /**
 * Triggers when sort changed by the user
 * @param sort
 */
  public sortChange(sort: SortDescriptor[]) {
    if (!sort || sort.length === 0 || sort[0].dir == undefined) {
      if (this.defaultSort?.length && this.defaultSort[0].field) {
        this.sort = [...this.defaultSort];
      } else {
        this.sort = sort || [];
      }
    } else {
      this.sort = sort;
    }
    this.runReport();
  }

  public getSortInfo(): SortInfo {
    let field = this.sort[0].field;
    let col = this.columns.find(col => col.field === field);
    let sortColumn = col != null ? col.sortIndex : 1;
    return { SortColumn: sortColumn, sortDirection: this.sort[0].dir === 'asc' ? 1 : 2 };
  }

  private isAllLocationsSelected(): boolean {
    const allLocations = this.commonData.locations || [];
    const selected = this.dataViewForm.get('locations')?.value || [];
    if (allLocations.length === 0) return false;
    if (selected.length !== allLocations.length) return false;
    const key = (x: any) => (x?.locationId ?? x?.code ?? '').toString();
    return allLocations.every((l: any) => selected.some((s: any) => key(s) === key(l)));
  }

  public toTitleCase(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }


  private readCastedValue(systemConfigValue: SystemConfigValue): any {
    if (systemConfigValue) {
      switch (systemConfigValue.dataType) {
        case 'int':
          return parseInt(systemConfigValue.value);
        case 'bool':
          return (systemConfigValue.value === '1');
        default:
          return systemConfigValue.value;
      }
    }
  }

  public nextLoadingPrompt() {
    this.loadingPrompt = this.loadingPrompt + ".";
  }
}
